import React, {
  useState,
  useMemo,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Upload } from "lucide-react";
import { useIsElectron } from "hooks/useIsElectron";
import SessionHeroSection from "./SessionHeroSection";
import SessionPlayerList from "./SessionPlayerList";
import SessionChart from "./SessionChart";
import SessionLoadChart from "./SessionLoadChart";
import ImpactHistoryModal from "./ImpactHistoryModal";
import WearableUploadModal from "./WearableUploadModal";
import DemoSettingsModal from "./DemoSettingsModal";
import EditSessionModal from "../EditSessionModal";
import TeamStatCard from "../TeamOverview/TeamStatCard";
import { MAGNITUDE_RANGES } from "./HeaderAnalysis";
import betaAPI from "v2/api";
import showToast from "utils/toast";
import PaneFrame, { PageFrame } from "../common/PaneFrame";

const DEFAULT_DEMO_SETTINGS = {
  minG: 5,
  maxG: 20,
  minDelaySec: 1,
  maxDelaySec: 20,
};

const getDemoStorageKey = (workspaceId, teamId, sessionId) =>
  `sit:demo:${workspaceId}:${teamId}:${sessionId}`;

const loadDemoStorage = (key) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const saveDemoStorage = (key, data) => {
  try {
    localStorage.setItem(key, JSON.stringify(data));
  } catch (err) {
    console.warn("Failed to persist demo data", err);
  }
};

const clearDemoStorage = (key) => {
  try {
    localStorage.removeItem(key);
  } catch {
    /* noop */
  }
};

const SessionOverview = () => {
  const { workspaceId, teamId, sessionId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isElectron = useIsElectron();

  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [selectedPlayerId, setSelectedPlayerId] = useState(null);
  const [showImpactHistory, setShowImpactHistory] = useState(false);
  const [showWearableUpload, setShowWearableUpload] = useState(false);
  const [isDemoMode, setIsDemoMode] = useState(false);
  const [flashingPlayerId, setFlashingPlayerId] = useState(null);
  const [demoSettings, setDemoSettings] = useState(DEFAULT_DEMO_SETTINGS);
  const [showDemoSettings, setShowDemoSettings] = useState(false);
  const [showEditSession, setShowEditSession] = useState(false);

  // Rename / change type (SPO-1538). Throws on failure so the dialog stays
  // open; the write clears the API cache, so lists refetch fresh.
  const handleSaveSession = async ({ name, type }) => {
    try {
      const { data } = await betaAPI.updateSession({
        workspaceId,
        teamId,
        sessionId,
        body: { name, type },
      });
      setSession((prev) => ({
        ...prev,
        name: data?.name ?? name,
        type: data?.type ?? type,
      }));
      showToast.success(t("session.editModal.saved"));
    } catch (err) {
      console.error("Failed to update session", err);
      showToast.error(t("session.editModal.saveFailed"));
      throw err;
    }
  };
  const [hasStoredDemoData, setHasStoredDemoData] = useState(false);
  // Synthetic impacts indexed by playerId — persisted to localStorage.
  // Kept as state so derived stats/charts re-render on each tick.
  const [demoImpactsByPlayer, setDemoImpactsByPlayer] = useState({});
  // Demo session start time (ISO string) — distinct from session.start_time so
  // we can show synthetic impacts on a "now"-aligned timeline without losing
  // the real session's start time.
  const [demoStartTime, setDemoStartTime] = useState(null);
  const demoTimerRef = useRef(null);
  const flashTimerRef = useRef(null);
  const assignmentsRef = useRef([]);
  const demoSettingsRef = useRef(DEFAULT_DEMO_SETTINGS);
  const demoActiveRef = useRef(false);
  const demoStartTimeRef = useRef(null);
  const demoImpactsByPlayerRef = useRef({});
  const hasHydratedRef = useRef(false);

  const demoStorageKey = useMemo(
    () => getDemoStorageKey(workspaceId, teamId, sessionId),
    [workspaceId, teamId, sessionId]
  );

  useEffect(() => {
    assignmentsRef.current = assignments;
  }, [assignments]);

  useEffect(() => {
    demoSettingsRef.current = demoSettings;
  }, [demoSettings]);

  useEffect(() => {
    demoActiveRef.current = isDemoMode;
  }, [isDemoMode]);

  useEffect(() => {
    demoImpactsByPlayerRef.current = demoImpactsByPlayer;
  }, [demoImpactsByPlayer]);

  useEffect(() => {
    demoStartTimeRef.current = demoStartTime;
  }, [demoStartTime]);

  const persistDemoState = useCallback(() => {
    saveDemoStorage(demoStorageKey, {
      active: demoActiveRef.current,
      startTime: demoStartTimeRef.current,
      impactsByPlayer: demoImpactsByPlayerRef.current,
      settings: demoSettingsRef.current,
    });
    setHasStoredDemoData(true);
  }, [demoStorageKey]);

  const fetchSession = useCallback(async () => {
    if (!workspaceId || !teamId || !sessionId) return;
    try {
      setLoading(true);
      const response = await betaAPI.getSession({
        workspaceId,
        teamId,
        sessionId,
      });
      setSession(response.data);
      setAssignments(response.data?.assignments || []);
    } catch (err) {
      console.error("Failed to fetch session", err);
      showToast.error(t("session.overview.loadFailed"));
    } finally {
      setLoading(false);
    }
  }, [workspaceId, teamId, sessionId, t]);

  useEffect(() => {
    fetchSession();
  }, [fetchSession]);

  // Superadmins additionally get each athlete's unit (field label, MAC, link
  // to the manufacturing record) via the admin roster endpoint. Best-effort:
  // if it fails the page just renders without unit details.
  const isSuperUser =
    JSON.parse(localStorage.getItem("user") || "null")?.is_superadmin === true;
  const [unitsByPlayerId, setUnitsByPlayerId] = useState(null);
  useEffect(() => {
    if (!isSuperUser || !sessionId) return;
    let active = true;
    betaAPI
      .getSessionWearables({ sessionId })
      .then((res) => {
        if (!active) return;
        const map = {};
        (res.data?.assignments?.results || []).forEach((r) => {
          if (!r.wearable_mac) return;
          map[r.player_id] = {
            mac: r.wearable_mac,
            bleMac: r.ble_mac,
            serial: r.serial,
            fieldLabel: r.field_label,
          };
        });
        setUnitsByPlayerId(map);
      })
      .catch((err) => console.error("Failed to load unit details", err));
    return () => {
      active = false;
    };
  }, [isSuperUser, sessionId]);

  // When in demo mode, swap each assignment's impact_data for the synthetic
  // impacts we've generated for that player. Real session data is preserved
  // in `assignments` and shown again as soon as demo mode is toggled off.
  const displayedAssignments = useMemo(() => {
    if (!isDemoMode) return assignments;
    return assignments.map((a) => {
      const aId = a.player_id || a.player?.id || a.id;
      const synth = demoImpactsByPlayer[aId] || [];
      return {
        ...a,
        impact_data: synth,
        impacts: synth.length,
      };
    });
  }, [isDemoMode, assignments, demoImpactsByPlayer]);

  const displayedStartTime = useMemo(() => {
    if (isDemoMode) return demoStartTime;
    return session?.start_time || null;
  }, [isDemoMode, demoStartTime, session]);

  // Derive player stats from displayed assignments
  const playerStats = useMemo(() => {
    return displayedAssignments.map((a) => {
      const impacts = a.impact_data || [];
      const peakG =
        impacts.length > 0
          ? Math.max(...impacts.map((i) => Number(i.linear_g) || 0))
          : 0;
      const playerName =
        a.player_name ||
        a.player?.name ||
        t("session.overview.athleteFallback", { id: a.player_id || a.id });
      let lastImpactTs = 0;
      for (const i of impacts) {
        const ts = new Date(i.timestamp).getTime();
        if (!isNaN(ts) && ts > lastImpactTs) lastImpactTs = ts;
      }
      const playerId = a.player_id || a.player?.id || a.id;
      return {
        id: playerId,
        name: playerName,
        impacts: impacts.length,
        peakG,
        wearableMac: a.wearable_mac || null,
        unit: unitsByPlayerId?.[playerId] || null,
        lastImpactTs,
      };
    });
  }, [displayedAssignments, unitsByPlayerId, t]);

  const maxPeakG = useMemo(() => {
    if (playerStats.length === 0) return 0;
    return Math.max(...playerStats.map((p) => p.peakG));
  }, [playerStats]);

  // Flatten all impacts with player names attached
  const allImpacts = useMemo(() => {
    const impacts = [];
    displayedAssignments.forEach((a) => {
      const playerName =
        a.player_name ||
        a.player?.name ||
        t("session.overview.athleteFallback", { id: a.player_id || a.id });
      const playerId = a.player_id || a.player?.id || a.id;
      (a.impact_data || []).forEach((impact) => {
        impacts.push({
          ...impact,
          playerName,
          playerId,
        });
      });
    });
    return impacts.sort(
      (a, b) => new Date(b.timestamp) - new Date(a.timestamp)
    );
  }, [displayedAssignments, t]);

  // Total stats
  const totalImpacts = allImpacts.length;
  const overallPeakG = useMemo(() => {
    if (allImpacts.length === 0) return 0;
    return Math.max(...allImpacts.map((i) => Number(i.linear_g) || 0));
  }, [allImpacts]);

  // Timeline buckets — variable granularity so early activity is legible:
  //   0–10 min:   30s buckets
  //   10–20 min:  1m buckets
  //   20m+:       5m buckets
  const timelineBuckets = useMemo(() => {
    if (!displayedStartTime || allImpacts.length === 0) return [];

    const startTime = new Date(displayedStartTime).getTime();
    const endTime = session?.end_time
      ? new Date(session.end_time).getTime()
      : startTime + 90 * 60000; // default 90 min

    const bucketSizeFor = (elapsedMs) => {
      if (elapsedMs < 10 * 60000) return 30 * 1000; // 30s
      if (elapsedMs < 20 * 60000) return 60 * 1000; // 1m
      return 5 * 60000; // 5m
    };

    const formatLabel = (elapsedMs, bucketSize) => {
      if (bucketSize < 60000) {
        const totalSeconds = Math.round(elapsedMs / 1000);
        const mins = Math.floor(totalSeconds / 60);
        const secs = totalSeconds % 60;
        return secs === 0
          ? `${mins}m`
          : `${mins}:${String(secs).padStart(2, "0")}`;
      }
      return `${Math.round(elapsedMs / 60000)}m`;
    };

    const buckets = [];
    let t = startTime;
    while (t < endTime) {
      const elapsedMs = t - startTime;
      const bucketSize = bucketSizeFor(elapsedMs);
      const bucketEnd = t + bucketSize;
      const allInBucket = allImpacts.filter((i) => {
        const ts = new Date(i.timestamp).getTime();
        return ts >= t && ts < bucketEnd;
      });
      const selectedInBucket = selectedPlayerId
        ? allInBucket.filter((i) => i.playerId === selectedPlayerId)
        : [];

      buckets.push({
        label: formatLabel(elapsedMs, bucketSize),
        allCount: allInBucket.length,
        selectedCount: selectedInBucket.length,
      });
      t = bucketEnd;
    }
    return buckets;
  }, [displayedStartTime, session, allImpacts, selectedPlayerId]);

  // Magnitude buckets
  const magnitudeBuckets = useMemo(() => {
    if (allImpacts.length === 0) return [];

    return MAGNITUDE_RANGES.map((range) => {
      const allInRange = allImpacts.filter((i) => {
        const g = Number(i.linear_g) || 0;
        return g >= range.min && g < range.max;
      });
      const selectedInRange = selectedPlayerId
        ? allInRange.filter((i) => i.playerId === selectedPlayerId)
        : [];

      return {
        label: range.label,
        allCount: allInRange.length,
        selectedCount: selectedInRange.length,
      };
    });
  }, [allImpacts, selectedPlayerId]);

  // Selected player object
  const selectedPlayer = playerStats.find((p) => p.id === selectedPlayerId);

  // Filtered impacts for modal
  const modalImpacts = useMemo(() => {
    if (selectedPlayerId) {
      return allImpacts.filter((i) => i.playerId === selectedPlayerId);
    }
    return allImpacts;
  }, [allImpacts, selectedPlayerId]);

  const handleBack = () => {
    navigate(`/v2/${workspaceId}/team/${teamId}`);
  };

  const handlePlayerSelect = (player) => {
    setSelectedPlayerId(player.id === selectedPlayerId ? null : player.id);
  };

  const scheduleNextDemoImpact = useCallback(() => {
    const settings = demoSettingsRef.current;
    const minMs = Math.max(100, settings.minDelaySec * 1000);
    const maxMs = Math.max(minMs, settings.maxDelaySec * 1000);
    // Skew toward shorter gaps so impacts cluster naturally rather than
    // landing one-per-bucket
    const skew = Math.pow(Math.random(), 2);
    const delay = minMs + skew * (maxMs - minMs);
    demoTimerRef.current = setTimeout(() => {
      const current = assignmentsRef.current;
      if (!current || current.length === 0) {
        scheduleNextDemoImpact();
        return;
      }
      const target = current[Math.floor(Math.random() * current.length)];
      const targetPlayerId = target.player_id || target.player?.id || target.id;
      const cur = demoSettingsRef.current;
      const gRange = Math.max(0, cur.maxG - cur.minG);
      const newImpact = {
        timestamp: new Date().toISOString(),
        linear_g: cur.minG + Math.random() * gRange,
        rotational: 1000 + Math.random() * 2000,
      };
      setDemoImpactsByPlayer((prev) => {
        const next = {
          ...prev,
          [targetPlayerId]: [...(prev[targetPlayerId] || []), newImpact],
        };
        demoImpactsByPlayerRef.current = next;
        return next;
      });
      persistDemoState();
      setFlashingPlayerId(null);
      requestAnimationFrame(() => {
        setFlashingPlayerId(targetPlayerId);
      });
      if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
      flashTimerRef.current = setTimeout(() => {
        setFlashingPlayerId(null);
      }, 800);
      scheduleNextDemoImpact();
    }, delay);
  }, [persistDemoState]);

  const handleToggleDemoMode = useCallback(() => {
    setIsDemoMode((wasOn) => {
      if (wasOn) {
        if (demoTimerRef.current) clearTimeout(demoTimerRef.current);
        if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
        demoTimerRef.current = null;
        flashTimerRef.current = null;
        setFlashingPlayerId(null);
        demoActiveRef.current = false;
        persistDemoState();
        return false;
      }
      if (assignmentsRef.current.length === 0) return false;
      // Fresh demo run: clear any prior synthetic impacts so we don't
      // pick up where a previous demo left off — the user wants to start
      // from zero. Resumes (auto-restart on mount via the hydration
      // effect) skip this branch entirely.
      const now = new Date().toISOString();
      setDemoStartTime(now);
      demoStartTimeRef.current = now;
      setDemoImpactsByPlayer({});
      demoImpactsByPlayerRef.current = {};
      demoActiveRef.current = true;
      persistDemoState();
      scheduleNextDemoImpact();
      return true;
    });
  }, [scheduleNextDemoImpact, persistDemoState]);

  const handleSaveSettings = useCallback(
    (next) => {
      setDemoSettings(next);
      demoSettingsRef.current = next;
      persistDemoState();
    },
    [persistDemoState]
  );

  const handleClearDemoData = useCallback(() => {
    if (demoTimerRef.current) clearTimeout(demoTimerRef.current);
    if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
    demoTimerRef.current = null;
    flashTimerRef.current = null;
    clearDemoStorage(demoStorageKey);
    demoImpactsByPlayerRef.current = {};
    demoStartTimeRef.current = null;
    demoActiveRef.current = false;
    setDemoImpactsByPlayer({});
    setDemoStartTime(null);
    setIsDemoMode(false);
    setFlashingPlayerId(null);
    setHasStoredDemoData(false);
  }, [demoStorageKey]);

  // Reset hydration flag if we switch to a different session
  useEffect(() => {
    hasHydratedRef.current = false;
    demoImpactsByPlayerRef.current = {};
    demoStartTimeRef.current = null;
    demoActiveRef.current = false;
    if (demoTimerRef.current) clearTimeout(demoTimerRef.current);
    if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
    demoTimerRef.current = null;
    flashTimerRef.current = null;
    setIsDemoMode(false);
    setFlashingPlayerId(null);
    setDemoImpactsByPlayer({});
    setDemoStartTime(null);
    setHasStoredDemoData(!!loadDemoStorage(demoStorageKey));
  }, [demoStorageKey]);

  // Hydrate demo state from localStorage once after the session loads
  useEffect(() => {
    if (loading || !session || hasHydratedRef.current) return;
    hasHydratedRef.current = true;
    const stored = loadDemoStorage(demoStorageKey);
    if (!stored) return;
    setHasStoredDemoData(true);
    if (stored.settings) {
      const merged = { ...DEFAULT_DEMO_SETTINGS, ...stored.settings };
      setDemoSettings(merged);
      demoSettingsRef.current = merged;
    }
    if (stored.startTime) {
      setDemoStartTime(stored.startTime);
      demoStartTimeRef.current = stored.startTime;
    }
    const byPlayer = stored.impactsByPlayer || {};
    setDemoImpactsByPlayer(byPlayer);
    demoImpactsByPlayerRef.current = byPlayer;
    if (stored.active) {
      demoActiveRef.current = true;
      setIsDemoMode(true);
      scheduleNextDemoImpact();
    }
  }, [loading, session, demoStorageKey, scheduleNextDemoImpact]);

  useEffect(() => {
    return () => {
      if (demoTimerRef.current) clearTimeout(demoTimerRef.current);
      if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
    };
  }, []);

  if (loading) {
    return (
      <div className="flex min-h-full items-center justify-center bg-[#F8F9FA] py-20">
        <div className="text-sm text-[#6B7280]">
          {t("session.overview.loading")}
        </div>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="flex min-h-full items-center justify-center bg-[#F8F9FA] py-20">
        <div className="text-sm text-[#6B7280]">
          {t("session.overview.notFound")}
        </div>
      </div>
    );
  }

  const hasImpactData = allImpacts.length > 0;

  return (
    <PageFrame>
      {/* Fixed header: hero. The frame below fills the rest of the window. */}
      <div
        className="flex-none"
        style={{
          background:
            "linear-gradient(180deg, rgba(232, 133, 74, 0.08) 0%, rgba(232, 133, 74, 0.03) 50%, rgba(255, 255, 255, 0) 100%)",
        }}
      >
        <div className="mx-auto max-w-[1440px]">
          <SessionHeroSection
            session={session}
            onBack={handleBack}
            isDemoMode={isDemoMode}
            onToggleDemoMode={handleToggleDemoMode}
            onOpenDemoSettings={() => setShowDemoSettings(true)}
            onEdit={() => setShowEditSession(true)}
          />
        </div>
      </div>

      {/* Athlete rail on the left, overview pane on the right; both end at
          the bottom of the window and scroll on their own. */}
      <PaneFrame
        rail={
          <SessionPlayerList
            players={playerStats}
            selectedPlayerId={selectedPlayerId}
            onPlayerSelect={handlePlayerSelect}
            maxPeakG={maxPeakG}
            flashingPlayerId={flashingPlayerId}
          />
        }
      >
        <div className="rounded-xl border border-[#F3F4F6] bg-white">
          {/* Card header */}
          <div className="flex items-center justify-between border-b border-[#F3F4F6] px-4 py-3">
            <h3 className="text-sm font-semibold text-[#111827]">
              {selectedPlayer
                ? selectedPlayer.name
                : t("session.overview.title")}
            </h3>
            {isElectron && (
              <button
                onClick={() => setShowWearableUpload(true)}
                className="flex items-center gap-1.5 rounded-lg border border-[#E5E7EB] px-3 py-1.5 text-xs font-medium text-[#374151] transition duration-150 hover:bg-[#F9FAFB]"
              >
                <Upload size={13} />
                {t("session.overview.uploadWearableData")}
              </button>
            )}
          </div>

          {/* Content */}
          <div className="p-4">
            {/* Tabs: Timeline / Magnitude / Load. Every tab shares the
                    same row structure (tab bar → 3 stat cards → legend →
                    chart → footer) so switching never shifts the layout. */}
            <SessionChart
              timelineBuckets={timelineBuckets}
              magnitudeBuckets={magnitudeBuckets}
              selectedPlayerName={selectedPlayer?.name || null}
              onViewImpactList={() => setShowImpactHistory(true)}
              hasData={hasImpactData}
              statsContent={
                <div className="mb-4 grid grid-cols-3 gap-3">
                  <TeamStatCard
                    label={t("session.overview.stats.totalImpacts")}
                    value={
                      hasImpactData
                        ? selectedPlayer
                          ? selectedPlayer.impacts
                          : totalImpacts
                        : "–"
                    }
                    footerLeft={
                      !hasImpactData
                        ? t("session.overview.stats.noDataYet")
                        : selectedPlayer
                        ? t("session.overview.stats.athleteImpacts")
                        : t("session.overview.stats.allAthletes")
                    }
                    footerRight={
                      hasImpactData
                        ? t("session.overview.stats.athleteTotal", {
                            total: playerStats.length,
                          })
                        : ""
                    }
                  />
                  <TeamStatCard
                    label={t("session.overview.stats.peakGForce")}
                    value={
                      !hasImpactData
                        ? "–"
                        : selectedPlayer
                        ? selectedPlayer.peakG.toFixed(1)
                        : overallPeakG.toFixed(1)
                    }
                    unit={hasImpactData ? "g" : undefined}
                    footerLeft={
                      !hasImpactData
                        ? t("session.overview.stats.noDataYet")
                        : selectedPlayer
                        ? t("session.overview.stats.athletePeak")
                        : t("session.overview.stats.sessionPeak")
                    }
                    footerRight={
                      !hasImpactData
                        ? ""
                        : selectedPlayer
                        ? t("session.overview.stats.sessionPeakValue", {
                            value: overallPeakG.toFixed(1),
                          })
                        : t("session.overview.stats.athleteTotal", {
                            total: playerStats.length,
                          })
                    }
                  />
                  <TeamStatCard
                    label={t("session.overview.stats.athletesRecorded")}
                    value={
                      hasImpactData
                        ? playerStats.filter((p) => p.impacts > 0).length
                        : "–"
                    }
                    footerLeft={
                      hasImpactData
                        ? t("session.overview.stats.withImpacts")
                        : t("session.overview.stats.noDataYet")
                    }
                    footerRight={
                      hasImpactData
                        ? t("session.overview.stats.ofAthletes", {
                            total: playerStats.length,
                          })
                        : ""
                    }
                  />
                </div>
              }
              loadContent={
                // Time-resolved low-g load (real data only — unaffected
                // by demo mode), embedded as the Load tab so the athlete
                // list stays alongside it.
                <SessionLoadChart
                  embedded
                  workspaceId={workspaceId}
                  teamId={teamId}
                  sessionId={sessionId}
                  sessionStartTime={session?.start_time || null}
                  sessionEndTime={session?.end_time || null}
                  selectedPlayerId={selectedPlayerId}
                  selectedPlayerName={selectedPlayer?.name || null}
                  onClearSelection={() => setSelectedPlayerId(null)}
                />
              }
            />
          </div>
        </div>
      </PaneFrame>

      {/* Impact History Modal */}
      <ImpactHistoryModal
        show={showImpactHistory}
        onClose={() => setShowImpactHistory(false)}
        impacts={modalImpacts}
        selectedPlayerName={selectedPlayer?.name || null}
      />

      {/* Demo Settings Modal */}
      <EditSessionModal
        show={showEditSession}
        onClose={() => setShowEditSession(false)}
        onSave={handleSaveSession}
        session={session}
      />
      <DemoSettingsModal
        show={showDemoSettings}
        onClose={() => setShowDemoSettings(false)}
        settings={demoSettings}
        onSave={handleSaveSettings}
        onClearData={handleClearDemoData}
        hasStoredData={hasStoredDemoData}
      />

      {/* Wearable Upload Modal */}
      <WearableUploadModal
        show={showWearableUpload}
        onClose={() => setShowWearableUpload(false)}
        assignments={assignments}
        workspaceId={workspaceId}
        teamId={teamId}
        sessionId={sessionId}
        onUploadComplete={fetchSession}
      />
    </PageFrame>
  );
};

export default SessionOverview;
